// START IMPORTS
import { CircularProgress, Theme } from '@ltvco/refresh-lib/theme';
import { getVisibleItems } from '@ltvco/refresh-lib/utils';
import { Owner, NavLinkDataV2 } from '@ltvco/refresh-lib/v2';
import { useFeatureIsOn } from '@ltvco/refresh-lib/vendors';
import {
  Phone,
  Radar,
  Person2,
  TaskAlt,
  Message,
  AccountCircle,
} from '@mui/icons-material';
// END IMPORTS

// START INTERFACES_AND_TYPES
type DataCounts = {
  potentialOwnersLength: number;
  searchVolumeLength: number;
  commentsLength: number;
  neighborSpoofingLength: number;
  spamReportsCount: number;
  associatedAccountsCount?: number | null;
  isAssociatedAccountsCountLoading?: boolean;
};

type IContactItemsForCount = {
  phones: Owner['phones'];
  emails: Owner['emails'];
  addresses: Owner['addresses'];
};
// END INTERFACES_AND_TYPES

// START HELPER_FUNCTIONS
export const getPhoneNavLinkData = (
  owner: Owner,
  dataCounts: DataCounts,
  contactItemsForCount?: IContactItemsForCount
) => {
  const assetsCount =
    owner.ownedAssets['real_estate'].length +
    owner.ownedAssets['vehicles'].aircraft.length +
    owner.ownedAssets['vehicles'].watercraft.length +
    owner.ownedAssets['vehicles'].automobiles.length;
  const contactInfoItems = contactItemsForCount ?? {
    phones: owner.phones,
    emails: owner.emails,
    addresses: owner.addresses,
  };
  const visiblePhoneCount = getVisibleItems(contactInfoItems.phones).length;
  const visibleEmailCount = getVisibleItems(contactInfoItems.emails).length;
  const visibleAddressCount = getVisibleItems(
    contactInfoItems.addresses
  ).length;
  const isPhonePartialAddress = useFeatureIsOn('OAR-1706');
  const isInAssociatedAccountsSectionVariation = useFeatureIsOn('RFRSH-4465');

  return [
    {
      href: '#phone-overview',
      sectionId: '#phone-overview',
      text: 'Overview',
      reportItemsCount: null,
      hideReportItemsCount: true,
      notificationSection: 'identity',
    },
    {
      href: '#phone-fraudscan-section',
      sectionId: '#phone-fraudscan-section',
      text: 'FraudScan Summary',
      reportItemsCount: null,
      hideReportItemsCount: true,
    },
    ...(isInAssociatedAccountsSectionVariation
      ? [
          getAssociatedAccountsStandaloneNavLinkData(
            dataCounts.associatedAccountsCount,
            dataCounts.isAssociatedAccountsCountLoading
          ),
        ]
      : []),
    {
      href: '#potential-owners-section',
      sectionId: '#potential-owners-section',
      text: 'Potential Owners',
      reportItemsCount: dataCounts.potentialOwnersLength,
      notificationSection: 'owners',
    },
    {
      href: '#phone-numbers-section',
      sectionId: '#phone-numbers-section',
      text: 'Other Phone Numbers',
      reportItemsCount: Math.max(visiblePhoneCount - 1, 0),
      notificationSection: 'phones',
    },
    {
      href: '#email-section',
      sectionId: '#email-section',
      text: 'Email Addresses',
      reportItemsCount: visibleEmailCount,
      notificationSection: 'emails',
    },
    {
      href: '#address-history-section',
      sectionId: '#address-history-section',
      text: isPhonePartialAddress ? 'Possible Locations' : 'Address History',
      reportItemsCount: visibleAddressCount,
      notificationSection: 'addresses',
    },
    {
      href: '#jobs-section',
      sectionId: '#jobs-section',
      text: 'Work History',
      reportItemsCount: owner.jobs.length,
      notificationSection: 'jobs',
    },
    {
      href: '#education-section',
      sectionId: '#education-section',
      text: 'Educational Background',
      reportItemsCount: owner.educations.length,
      notificationSection: 'educations',
    },
    {
      href: '#social-media-section',
      sectionId: '#social-media-section',
      text: 'Social Media',
      reportItemsCount: owner.profiles.length + owner.usernames.length,
      notificationSection: 'socials',
    },
    {
      href: '#neighbor-spoofing-section',
      sectionId: '#neighbor-spoofing-section',
      text: 'Neighbor Spoofing',
      reportItemsCount: dataCounts.neighborSpoofingLength,
      notificationSection: 'neighbors',
    },
    {
      href: '#search-volume-section',
      sectionId: '#search-volume-section',
      text: 'Search Volume',
      reportItemsCount: dataCounts.searchVolumeLength,
    },
    {
      href: '#nuisance-caller-section',
      sectionId: '#nuisance-caller-section',
      text: 'Nuisance Caller',
      reportItemsCount: dataCounts.spamReportsCount,
    },
    {
      href: '#comments-section',
      sectionId: '#comments-section',
      text: 'Comments',
      reportItemsCount: dataCounts.commentsLength,
    },
    {
      href: '#possible-owned-assets-section',
      sectionId: '#possible-owned-assets-section',
      text: 'Assets',
      reportItemsCount: assetsCount,
      notificationSection: 'real_estate',
    },
  ];
};

export const getPhoneNavLinkDataV2 = (
  owner: Owner,
  dataCounts: DataCounts,
  theme: Theme,
  contactItemsForCount?: IContactItemsForCount
) => {
  const assetsCount =
    owner.ownedAssets['real_estate'].length +
    owner.ownedAssets['vehicles'].aircraft.length +
    owner.ownedAssets['vehicles'].watercraft.length +
    owner.ownedAssets['vehicles'].automobiles.length;

  const isPhonePartialAddress = useFeatureIsOn('OAR-1706');
  const isInAssociatedAccountsSectionVariation = useFeatureIsOn('RFRSH-4465');
  const contactInfoItems = contactItemsForCount ?? {
    phones: owner.phones,
    emails: owner.emails,
    addresses: owner.addresses,
  };
  const visiblePhoneCount = getVisibleItems(contactInfoItems.phones).length;
  const visibleEmailCount = getVisibleItems(contactInfoItems.emails).length;
  const visibleAddressCount = getVisibleItems(
    contactInfoItems.addresses
  ).length;

  let potentialOwnerNumbers = 0;

  if (dataCounts.potentialOwnersLength > 0) {
    potentialOwnerNumbers = dataCounts.potentialOwnersLength;
  }

  const fraudSectionItems = [
    {
      labelText: 'Threat Detection',
      href: '#threat-detection-section',
      icons: [
        <Radar
          sx={{ color: theme.palette.primary.main, fontSize: '1.4rem' }}
          key="threat-detection-section"
        />,
      ],
      name: 'threat-detection-section',
      hideReportItemsCount: true,
    },
  ];

  const PotentialOwnerChildItems = [
    {
      href: '#potential-owners-section',
      labelText: `Potential ${potentialOwnerNumbers > 1 ? 'Owners' : 'Owner'}`,
      reportItemsCount: potentialOwnerNumbers,
      notificationSection: 'owners',
      name: 'potential-owners-section',
    },
    {
      href: '#phone-numbers-section',
      labelText: 'Phone Numbers',
      reportItemsCount: Math.max(visiblePhoneCount - 1, 0),
      notificationSection: 'phones',
      name: 'phone-numbers-section',
    },
    {
      href: '#email-section',
      labelText: 'Email Addresses',
      reportItemsCount: visibleEmailCount,
      notificationSection: 'emails',
      name: 'email-section',
    },
    {
      href: '#address-history-section',
      labelText: isPhonePartialAddress
        ? 'Possible Locations'
        : 'Address History',
      reportItemsCount: visibleAddressCount,
      notificationSection: 'addresses',
      name: 'address-history-section',
    },
    {
      href: '#social-media-section',
      sectionId: '#social-media-section',
      labelText: 'Social Media',
      reportItemsCount: owner.profiles.length + owner.usernames.length,
      notificationSection: 'social',
      name: 'social-media-section',
    },
    {
      href: '#jobs-section',
      sectionId: '#jobs-section',
      labelText: 'Jobs',
      reportItemsCount: owner.jobs.length,
      notificationSection: 'jobs',
      name: 'jobs-section',
    },
    {
      href: '#education-section',
      sectionId: '#education-section',
      labelText: 'Education',
      reportItemsCount: owner.educations.length,
      notificationSection: 'educations',
      name: 'education-section',
    },
    {
      labelText: 'Assets',
      href: '#possible-owned-assets-section',
      reportItemsCount: assetsCount,
      notificationSection: 'real_estate',
      name: 'assets-section',
    },
  ];

  return [
    {
      href: '#phone-overview',
      labelText: 'Overview',
      icons: [
        <Phone
          sx={{ color: theme.palette.primary.main, fontSize: '2.5rem' }}
          key="phone-overview"
        />,
      ],
      name: 'phone-overview',
    },
    {
      labelText: 'Potential Owners',
      icons: [
        <Person2
          sx={{ color: theme.palette.primary.main, fontSize: '1.4rem' }}
          key="potential-owners"
        />,
      ],
      name: 'potential-owners',
      childItems: PotentialOwnerChildItems,
    },
    ...(isInAssociatedAccountsSectionVariation
      ? [
          getAssociatedAccountsStandaloneNavItem(
            theme,
            dataCounts.associatedAccountsCount,
            dataCounts.isAssociatedAccountsCountLoading
          ),
        ]
      : []),
    ...fraudSectionItems,
    {
      href: '#comments-section',
      sectionId: '#comments-section',
      labelText: 'Comments',
      icons: [<Message sx={{ color: theme.palette.primary.main }} />],
      reportItemsCount: dataCounts.commentsLength,
    },
    {
      labelText: 'Next Steps',
      icons: [
        <TaskAlt
          sx={{ color: theme.palette.primary.main, fontSize: '1.4rem' }}
          key="next-steps"
        />,
      ],
      name: 'next-steps',
      childItems: [
        {
          href: '#Notes-section',
          labelText: 'Notes',
          hideReportItemsCount: true,
          name: 'notes-section',
        },
        {
          href: '#related-reports-section',
          labelText: 'Related Reports',
          hideReportItemsCount: true,
          name: 'related-reports-section',
        },
        {
          href: '#report-rating',
          labelText: 'Report Rating',
          hideReportItemsCount: true,
          name: 'report-rating',
        },
      ],
    },
  ];
};

export const getAssociatedAccountsStandaloneNavLinkData = (
  associatedAccountsCount?: number | null,
  isAssociatedAccountsCountLoading?: boolean
) => ({
  href: '#associated-accounts-section',
  sectionId: '#associated-accounts-section',
  text: 'Associated Accounts',
  reportItemsCount: isAssociatedAccountsCountLoading
    ? null
    : associatedAccountsCount ?? null,
  hideReportItemsCount: isAssociatedAccountsCountLoading,
  icon: isAssociatedAccountsCountLoading ? (
    <CircularProgress size={16} aria-label="Loading count" />
  ) : undefined,
  showIcon: isAssociatedAccountsCountLoading,
  notificationSection: 'associated-accounts-section',
  name: 'associated-accounts-section',
});

export const getAssociatedAccountsStandaloneNavItem = (
  theme: Theme,
  associatedAccountsCount?: number | null,
  isAssociatedAccountsCountLoading?: boolean
): NavLinkDataV2 =>
  ({
    href: '#associated-accounts-section',
    labelText: 'Associated Accounts',
    icons: [
      <AccountCircle
        sx={{ color: theme.palette.primary.main, fontSize: '1.4rem' }}
        key="associated-accounts-section"
      />,
    ],
    reportItemsCount: isAssociatedAccountsCountLoading
      ? null
      : associatedAccountsCount ?? null,
    isReportItemsCountLoading: isAssociatedAccountsCountLoading,
    notificationSection: 'associated-accounts-section',
  } as NavLinkDataV2);
// END HELPER_FUNCTIONS
